// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ListItem.tsx
 * @input Uses React, ReactNode, StyleXStyles, theme tokens
 * @output Exports ListItem component, ListItemProps type
 * @position Core implementation; consumed by List, index.ts, tested by List.test.tsx
 *
 * Composes Item for the shared start content + label + description + end content layout
 * and the invisible button/anchor interactive pattern.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/List/List.doc.mjs
 * - /packages/core/src/List/List.test.tsx
 * - /packages/core/src/List/index.ts
 * - /apps/storybook/stories/List.stories.tsx
 * - /packages/cli/templates/blocks/components/List/ (showcase blocks)
 */
import { use } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, typeScaleVars, borderVars } from "../theme/tokens.stylex.js";
import { ListContext } from "./ListContext.js";
import { mergeProps } from "../utils/index.js";
import { Item } from "../Item/index.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Types
// =============================================================================
import { jsx as _jsx } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  withCounter: {
    kAmcRD: "xfrknyr",
    $$css: true
  },
  withDivider: {
    kt9PQ7: "x92x3c3",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "xw8gpjh",
    $$css: true
  }
};

// =============================================================================
// Marker styles — custom-rendered markers instead of native list-style-type.
// Uses CSS counters for numbers (same pattern as WWW Astryx).
// =============================================================================

const MARKER_DOT_SIZE = 6;
const embeddedStyles = {
  noRadius: {
    kaIpWk: "x2u8bby",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * A list item component for use within List.
 *
 * Renders structured content with label, description, start/end content areas.
 * When `onClick` is provided, uses the invisible button pattern for accessibility.
 * When `href` is provided, uses an invisible anchor pattern.
 *
 * @example
 * ```
 * <ListItem label="Settings" description="Manage your preferences" />
 * <ListItem label="Profile" onClick={() => navigate('/profile')} />
 * <ListItem label="Docs" href="/docs" target="_blank" rel="noreferrer" />
 * ```
 */
export function ListItem({
  label,
  description,
  startContent,
  endContent,
  onClick,
  href,
  target,
  rel,
  isDisabled = false,
  isSelected = false,
  xstyle,
  className,
  style,
  ref,
  ...restProps
}) {
  const ctx = use(ListContext);
  const density = ctx?.density ?? 'balanced';
  const hasDividers = ctx?.hasDividers ?? false;
  const listStyle = ctx?.listStyle ?? 'none';
  const hasMarkers = listStyle !== 'none';
  const marker = listStyle === 'disc' ? /*#__PURE__*/_jsx("span", {
    ...{
      className: "xoi2r2e x9f619 x78zum5 x6s0dn4 xl56j7k x2lah0s x12xnipv x1233pnv"
    },
    children: /*#__PURE__*/_jsx("span", {
      ...{
        className: "x1v4s8kt xols6we x16rqkct x19aspcf"
      }
    })
  }) : listStyle === 'circle' ? /*#__PURE__*/_jsx("span", {
    ...{
      className: "xoi2r2e x9f619 x78zum5 x6s0dn4 xl56j7k x2lah0s x12xnipv x1233pnv"
    },
    children: /*#__PURE__*/_jsx("span", {
      ...{
        className: "x1v4s8kt xols6we x16rqkct xmkeg23 x1y0btm7 xqcx1ss xjbqb8w"
      }
    })
  }) : listStyle === 'decimal' ? /*#__PURE__*/_jsx("span", {
    ...{
      className: "xoi2r2e x2lah0s x1tgivj0 xjm74w1 xw6l6zx x12xnipv xc2ndz5"
    }
  }) : null;
  return /*#__PURE__*/_jsx(Item, {
    as: "li",
    ref: ref,
    marker: marker,
    startContent: startContent,
    label: label,
    description: description,
    endContent: endContent,
    onClick: onClick,
    href: href,
    target: target,
    rel: rel,
    isDisabled: isDisabled,
    isSelected: isSelected,
    density: density,
    xstyle: [hasMarkers && styles.withCounter, hasDividers && styles.withDivider, hasDividers && embeddedStyles.noRadius, xstyle],
    ...mergeProps(themeProps('list-item'), {
      className,
      style
    }),
    ...restProps
  });
}
ListItem.displayName = 'ListItem';